// Phase 19 · Community V2 · Notification bell (dropdown + polling)
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Bell, MessageCircle, AtSign, Users, Check } from "lucide-react";
import useCommunityPolling from "../../hooks/useCommunityPolling";

function fmtRel(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const diff = (Date.now() - d.getTime()) / 1000;
        if (diff < 60) return Math.floor(diff) + "s";
        if (diff < 3600) return Math.floor(diff / 60) + "m";
        if (diff < 86400) return Math.floor(diff / 3600) + "h";
        return Math.floor(diff / 86400) + "d";
    } catch (e) { return ""; }
}

const EVT_ICON = {
    new_dm: <MessageCircle size={14} className="text-[#CCCCCC]" />,
    dm_reply: <MessageCircle size={14} className="text-[#CCCCCC]" />,
    channel_mention: <AtSign size={14} className="text-[#FFFFFF]" />,
    group_new_message: <Users size={14} className="text-[#CCCCCC]" />,
    group_invite: <Users size={14} className="text-[#FFFFFF]" />,
};

function EventLine({ n }) {
    const ref = n.entity_ref || {};
    if (n.event_type === "new_dm" || n.event_type === "dm_reply") {
        const who = ref.sender_username || "user";
        return (
            <Link
                to={`/messages/${who}`}
                className="flex items-start gap-2 px-3 py-2 hover:bg-white/5 rounded"
            >
                {EVT_ICON[n.event_type]}
                <div className="flex-1 min-w-0">
                    <p className="text-xs text-neutral-200">
                        <b>@{who}</b> sent you a message
                    </p>
                    {n.body_preview && (
                        <p className="text-xs text-neutral-500 truncate mt-0.5">{n.body_preview}</p>
                    )}
                </div>
                <span className="text-[10px] text-neutral-500">{fmtRel(n.created_at)}</span>
            </Link>
        );
    }
    if (n.event_type === "channel_mention") {
        return (
            <Link
                to={ref.group_id ? `/groups/${ref.group_id}` : "/community"}
                className="flex items-start gap-2 px-3 py-2 hover:bg-white/5 rounded"
            >
                {EVT_ICON.channel_mention}
                <div className="flex-1 min-w-0">
                    <p className="text-xs text-neutral-200">
                        <b>@{ref.sender_username}</b> mentioned you
                    </p>
                    {n.body_preview && (
                        <p className="text-xs text-neutral-500 truncate mt-0.5">{n.body_preview}</p>
                    )}
                </div>
                <span className="text-[10px] text-neutral-500">{fmtRel(n.created_at)}</span>
            </Link>
        );
    }
    return (
        <div className="flex items-start gap-2 px-3 py-2 hover:bg-white/5 rounded">
            {EVT_ICON[n.event_type] || <Bell size={14} className="text-neutral-400" />}
            <div className="flex-1 min-w-0">
                <p className="text-xs text-neutral-200">{n.event_type.replace(/_/g, " ")}</p>
                {n.body_preview && (
                    <p className="text-xs text-neutral-500 truncate mt-0.5">{n.body_preview}</p>
                )}
            </div>
            <span className="text-[10px] text-neutral-500">{fmtRel(n.created_at)}</span>
        </div>
    );
}

export default function NotificationBell({ enabled = true }) {
    const [open, setOpen] = useState(false);
    const { items, unread_count, markAllRead } = useCommunityPolling(30000, enabled);

    return (
        <div className="relative" data-testid="notification-bell-container">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="relative w-9 h-9 rounded-full bg-[#111111] border border-[#1A1A1A] hover:border-[#FFFFFF]/40 flex items-center justify-center transition-colors"
                aria-label="Notifications"
                data-testid="notification-bell-btn"
            >
                <Bell size={16} className="text-neutral-300" />
                {unread_count > 0 && (
                    <span
                        className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#FAFAFA] text-[10px] font-bold text-white flex items-center justify-center"
                        data-testid="notification-badge"
                    >
                        {unread_count > 9 ? "9+" : unread_count}
                    </span>
                )}
            </button>
            {open && (
                <div
                    className="absolute right-0 top-11 w-80 max-h-96 overflow-y-auto bg-[#111111] border border-[#1A1A1A] rounded-xl shadow-2xl z-50"
                    data-testid="notification-dropdown"
                >
                    <div className="flex items-center justify-between px-3 py-2 border-b border-[#1A1A1A] sticky top-0 bg-[#111111]">
                        <p className="text-xs font-semibold text-neutral-200">Notifications</p>
                        {unread_count > 0 && (
                            <button
                                type="button"
                                onClick={markAllRead}
                                className="inline-flex items-center gap-1 text-[10px] text-[#FFFFFF] hover:text-[#CCCCCC]"
                                data-testid="notification-mark-all-read"
                            >
                                <Check size={11} /> Mark all read
                            </button>
                        )}
                    </div>
                    <div className="py-1">
                        {items.length === 0 ? (
                            <p className="text-xs text-neutral-500 italic px-3 py-6 text-center">
                                No notifications yet
                            </p>
                        ) : (
                            items.map((n) => (
                                <div key={n.id} className={n.read ? "opacity-60" : ""}>
                                    <EventLine n={n} />
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
